import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import {
	Button,
	Card,
	Divider,
	Drawer,
	NumberInput,
	Slider,
	TextInput,
	CardProps,
	SliderProps,
} from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { useSplitTransactionDrawerController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/SplitTransactionDrawer/useSplitTransactionDrawerController.ts';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { GetTransactionRes } from '@dealstack/shared/lib/generated';
import { extractDecimalPrecisionFromUnitDenominator } from '@dealstack/shared/lib/format/number.ts';
import { formatTransactionUnits } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/utils.ts';
import { StyledForm } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/styled.tsx';
import { splitCostByPercentage } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/SplitTransactionDrawer/splitTransaction.utils.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	refreshTransactionsList: () => void;
	selectedEntityName: string;
	selectedEntityRegNumber?: string;
	transaction: GetTransactionRes;
}

export const SplitTransactionDrawer = ({
	opened,
	onClose,
	refreshTransactionsList,
	transaction,
	selectedEntityName,
	selectedEntityRegNumber,
}: Props) => {
	const TOTAL_AMOUNT = transaction.units ?? 0;
	const TOTAL_PRICE = transaction.cost ?? 0;
	const SECURITY_PRECISION = extractDecimalPrecisionFromUnitDenominator(
		transaction.securityUnitDenominator
	);
	const tCapManage = useTranslationCapManage();
	const [slider1Value, setSlider1Value] = useState(50);
	const [slider2Value, setSlider2Value] = useState(50);
	const [amount1Value, setAmount1Value] = useState<number>((TOTAL_AMOUNT * 50) / 100);
	const [amount2Value, setAmount2Value] = useState<number>((TOTAL_AMOUNT * 50) / 100);
	const [price1Value, setPrice1Value] = useState(splitCostByPercentage(TOTAL_PRICE, 50)[0]);
	const [price2Value, setPrice2Value] = useState(splitCostByPercentage(TOTAL_PRICE, 50)[1]);
	const [amountError, setAmountError] = useState('');

	const { splitTransactionForm, onSubmit, splitTransactionsRequest } =
		useSplitTransactionDrawerController({
			transaction,
			refreshTransactionsList,
			onClose,
		});

	useEffect(() => {
		setSlider1Value(50);
		setSlider2Value(50);
		setAmount1Value(Math.ceil(TOTAL_AMOUNT / 2));
		setAmount2Value(TOTAL_AMOUNT - Math.ceil(TOTAL_AMOUNT / 2));
		const [price1, price2] = splitCostByPercentage(TOTAL_PRICE, 50);
		setPrice1Value(price1);
		setPrice2Value(price2);
	}, [opened]);

	const handleSlider1Change = (value: number) => {
		const roundedValue = Math.round(value);
		const split1 = Math.round((TOTAL_AMOUNT * roundedValue) / 100);
		const split2 = TOTAL_AMOUNT - split1;
		const [price1, price2] = splitCostByPercentage(TOTAL_PRICE, value);

		setSlider1Value(roundedValue);
		setSlider2Value(100 - roundedValue);
		setAmount1Value(split1);
		setAmount2Value(split2);
		setPrice1Value(price1);
		setPrice2Value(price2);
		setAmountError('');
		splitTransactionForm.setValue('units1', split1);
		splitTransactionForm.setValue('units2', split2);
		splitTransactionForm.setValue('cost1', price1);
		splitTransactionForm.setValue('cost2', price2);
	};

	const handleSlider2Change = (value: number) => {
		const roundedValue = Math.round(value);
		const split2 = Math.round((TOTAL_AMOUNT * roundedValue) / 100);
		const split1 = TOTAL_AMOUNT - split2;
		const [price2, price1] = splitCostByPercentage(TOTAL_PRICE, value);

		setSlider2Value(roundedValue);
		setSlider1Value(100 - roundedValue);
		setAmount1Value(Math.round(split1));
		setAmount2Value(Math.round(split2));
		setPrice1Value(price1);
		setPrice2Value(price2);
		setAmountError('');
		splitTransactionForm.setValue('units2', split2);
		splitTransactionForm.setValue('units1', split1);
		splitTransactionForm.setValue('cost2', price2);
		splitTransactionForm.setValue('cost1', price1);
	};

	const handleAmount1Change = (value: number) => {
		setAmount1Value(Math.round(value));

		if (!isNaN(value)) {
			if (value > TOTAL_AMOUNT) {
				setAmountError(`Amount cannot exceed ${TOTAL_AMOUNT}`);
			} else {
				const split1 = value;
				const split2 = TOTAL_AMOUNT - split1;
				const slider1 = (split1 / TOTAL_AMOUNT) * 100;
				const [price1, price2] = splitCostByPercentage(TOTAL_PRICE, slider1);

				setAmount1Value(split1);
				setAmount2Value(split2);
				setSlider1Value(Math.round(slider1));
				setSlider2Value(100 - Math.round(slider1));
				setPrice1Value(price1);
				setPrice2Value(price2);
				setAmountError('');
				splitTransactionForm.setValue('units1', split1);
				splitTransactionForm.setValue('units2', split2);
				splitTransactionForm.setValue('cost1', price1);
				splitTransactionForm.setValue('cost2', price2);
			}
		}
	};

	const handleAmount2Change = (value: number) => {
		setAmount2Value(Math.round(value));

		if (!isNaN(value)) {
			if (value > TOTAL_AMOUNT) {
				setAmountError(`Amount cannot exceed ${TOTAL_AMOUNT}`);
			} else {
				const split2 = value;
				const split1 = TOTAL_AMOUNT - split2;
				const slider2 = (split2 / TOTAL_AMOUNT) * 100;
				const [price2, price1] = splitCostByPercentage(TOTAL_PRICE, slider2);

				setAmount2Value(split2);
				setAmount1Value(split1);
				setSlider2Value(Math.round(slider2));
				setSlider1Value(100 - Math.round(slider2));
				setPrice1Value(price1);
				setPrice2Value(price2);
				setAmountError('');
				splitTransactionForm.setValue('units2', split2);
				splitTransactionForm.setValue('units1', split1);
				splitTransactionForm.setValue('cost2', price2);
				splitTransactionForm.setValue('cost1', price1);
			}
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={tCapManage('transactions.drawer.split_title')}
		>
			{splitTransactionsRequest.isPending && <InlineLoader />}
			<FormProvider {...splitTransactionForm}>
				<StyledForm onSubmit={splitTransactionForm.handleSubmit(onSubmit)}>
					<TransactionCard>
						<TransactionTextTitle>
							<NameCircle>
								{selectedEntityName
									.split(' ')
									.map((word) => word[0])
									.slice(0, 2)
									.join('')
									.toUpperCase()}
							</NameCircle>
							{selectedEntityRegNumber ? (
								<NameContent>
									<BoldTitle>{selectedEntityName}</BoldTitle>
									<DescriptionTitle>{selectedEntityRegNumber}</DescriptionTitle>
								</NameContent>
							) : (
								<NameSoloContent>
									<BoldTitle>{selectedEntityName}</BoldTitle>
								</NameSoloContent>
							)}
						</TransactionTextTitle>

						<Divider my="sm" color="#E9EBED" />
						<TransactionTextDescription>
							<NameContent>
								<BoldTitle>
									{tCapManage('transactions.drawer.price.label')}
								</BoldTitle>
								<DetailValue>
									{transaction?.cost?.toLocaleString() || LONG_DASH}
								</DetailValue>
							</NameContent>
							<NameContent>
								<BoldTitle>
									{tCapManage('transactions.drawer.number_of_units.label')}
								</BoldTitle>
								<DetailValue>{formatTransactionUnits(transaction)}</DetailValue>
							</NameContent>
						</TransactionTextDescription>
					</TransactionCard>

					<SubTransactionCard>
						<SliderLabel>{tCapManage('transactions.drawer.split.label')} 1</SliderLabel>
						<InputContainer>
							<div style={{ width: '100%' }}>
								<InputLabel>Cost</InputLabel>
								<TextInput
									type="number"
									placeholder="Cost"
									value={price1Value.toFixed(2)}
									readOnly
									disabled
									style={{ width: '100%' }}
								/>
							</div>

							<div style={{ width: '100%' }}>
								<InputLabel>Units</InputLabel>
								<NumberInput
									value={amount1Value}
									onChange={handleAmount1Change}
									error={amountError}
									step={1}
									min={0}
									max={TOTAL_AMOUNT}
									decimalScale={SECURITY_PRECISION}
									style={{ width: '100%' }}
								/>
							</div>
						</InputContainer>
						<StyledSlider
							min={0}
							max={100}
							step={1}
							value={slider1Value}
							onChange={handleSlider1Change}
							label={(value) => `${value}%`}
							marks={[
								{ value: 0, label: '0%' },
								{ value: 50, label: '50%' },
								{ value: 100, label: '100%' },
							]}
						/>
					</SubTransactionCard>

					<SubTransactionCard>
						<SliderLabel>{tCapManage('transactions.drawer.split.label')} 2</SliderLabel>
						<InputContainer>
							<div style={{ width: '100%' }}>
								<InputLabel>Cost</InputLabel>
								<TextInput
									type="number"
									placeholder="Cost"
									value={price2Value.toFixed(2)}
									readOnly
									disabled
									style={{ width: '100%' }}
								/>
							</div>
							<div style={{ width: '100%' }}>
								<InputLabel>Units</InputLabel>
								<NumberInput
									value={amount2Value}
									onChange={handleAmount2Change}
									error={amountError}
									step={1}
									min={0}
									max={TOTAL_AMOUNT}
									decimalScale={SECURITY_PRECISION}
									style={{ width: '100%' }}
								/>
							</div>
						</InputContainer>

						<StyledSlider
							min={0}
							max={100}
							step={1}
							value={slider2Value}
							onChange={handleSlider2Change}
							label={(value) => `${value}%`}
							marks={[
								{ value: 0, label: '0%' },
								{ value: 50, label: '50%' },
								{ value: 100, label: '100%' },
							]}
						/>
					</SubTransactionCard>

					<Button type="submit" fullWidth mt="auto">
						{tCapManage('transactions.drawer.save_button')}
					</Button>
					<Button fullWidth variant="default" onClick={onClose}>
						{tCapManage('securities.details.cancel_button')}
					</Button>
				</StyledForm>
			</FormProvider>
		</Drawer>
	);
};

const TransactionCard = styled(Card)<CardProps>`
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	border: none;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
`;

export const TransactionTextTitle = styled.div`
	display: flex;
	gap: 16px;
`;

export const TransactionTextDescription = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

export const NameCircle = styled.div`
	border-radius: 50%;
	background-color: ${({ theme }) => theme.v2.color.neutral[200]};
	width: 48px;
	height: 48px;
	display: flex;
	justify-content: center;
	align-items: center;
	font-weight: 400;
	font-size: 17px;
`;

export const NameContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const NameSoloContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

export const BoldTitle = styled.div`
	font-weight: 600;
	font-size: 14px;
	line-height: 20px;
`;

export const DescriptionTitle = styled.div`
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	color: #4f616c;
`;

export const DetailValue = styled.div`
	font-size: 25px;
	line-height: 32px;
	font-weight: 200;
	color: #0e171c;
`;

const InputContainer = styled.div`
	display: flex;
	gap: 8px;
	margin-top: 8px;
	margin-bottom: 8px;
	width: 100%;
`;

const SubTransactionCard = styled(Card)<CardProps>`
	border: 1px solid #e9ebed;
	border-radius: 8px;
	margin-top: 16px;
	padding: 16px 16px 48px;
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const SliderLabel = styled.div`
	font-size: 14px;
	font-weight: 600;
	padding-bottom: 16px;
	margin-left: -16px;
	margin-right: -16px;
	padding-left: 16px;
	padding-right: 16px;
	border-bottom: 1px solid #e9ebed;
`;

const InputLabel = styled.div`
	font-size: 14px;
	font-weight: 600;
`;

const StyledSlider = styled(Slider)<SliderProps>`
	.mantine-Slider-markLabel {
		color: #0e171c;
		font-weight: 400;
		font-size: 14px;
		line-height: 20px;
		margin-top: 16px;
	}

	.mantine-Slider-thumb {
		width: 24px;
		height: 24px;
		border-radius: 50%;
		border: 2px solid #0d5ef2;
		background-color: white;
		box-shadow: none;
	}

	.mantine-Slider-mark {
		background-color: transparent;
		width: 0;
		height: 0;
	}
`;
